import { motion } from 'framer-motion'
import { Link } from '@tanstack/react-router'
import { 
  Sparkles, 
  ArrowRight,
  Cpu, 
  Printer, 
  ScanLine, 
  Store,
  MessageSquare,
  Wand2,
  Image,
  Layers,
  GitBranch,
  Users,
  Eye,
  AlertTriangle,
  Timer,
  Camera,
  Wifi,
  BarChart3,
  Smartphone,
  Box,
  Search,
  ShoppingCart,
  Truck,
  CheckCircle,
  Zap
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import BetaBadge from '@/components/ui/BetaBadge'
import MarketingNav from '@/components/marketing/MarketingNav'
import MarketingFooter from '@/components/marketing/MarketingFooter'

const featureCategories = [
  {
    id: 'ai-cad',
    icon: Cpu,
    title: 'AI-Native CAD',
    tagline: 'Design with intelligence, not just tools',
    description: 'Every interaction in 3DMakerly is augmented by AI that understands design intent. From natural language commands to intelligent feature suggestions, your AI co-pilot accelerates every step.',
    gradient: 'from-primary-500 to-ai-500',
    features: [
      {
        icon: MessageSquare,
        title: 'Natural Language Commands',
        description: '"Add a 2mm fillet to all vertical edges"  -  speak to your CAD like you speak to a colleague. Our AI understands design terminology and executes precisely.',
      },
      {
        icon: Wand2,
        title: 'Text-to-3D Generation',
        description: 'Describe what you need, get printable geometry in seconds. Not mesh output  -  real parametric models with editable feature trees.',
      },
      {
        icon: Image,
        title: 'Image-to-3D Conversion',
        description: 'Upload a photo, sketch, or reference image. AI extracts geometry and creates editable 3D models with proper dimensional relationships.',
      },
      {
        icon: Layers,
        title: 'AI Feature Suggestions',
        description: 'As you design, AI suggests relevant operations based on your design patterns, industry best practices, and manufacturing requirements.',
      },
      {
        icon: GitBranch,
        title: 'Parametric Intelligence',
        description: 'AI maintains constraint relationships, suggests dimensional changes, and predicts the impact of modifications throughout your model.',
      },
      {
        icon: Users,
        title: 'Real-Time Collaboration',
        description: 'Work together with your team. AI handles merge conflicts, suggests resolutions, and maintains design integrity across simultaneous edits.',
      },
    ],
  },
  {
    id: 'print-intelligence',
    icon: Printer,
    title: 'Predictive Print Intelligence',
    tagline: 'Know before you print',
    description: 'PrintPredict analyses every design against your specific printer, material, and settings to predict outcomes before you consume material or time. 92%+ accuracy on success prediction.',
    gradient: 'from-ai-500 to-success-500',
    features: [
      {
        icon: Eye,
        title: 'Success Prediction',
        description: '92%+ accuracy predicting print success or failure. Understand exactly which features pose risks before starting your print.',
      },
      {
        icon: AlertTriangle,
        title: 'Failure Mode Analysis',
        description: 'Detect potential adhesion failures, layer separation, stringing, warping, bridging issues  -  with specific prevention recommendations.',
      },
      {
        icon: Timer,
        title: 'Accurate Time & Cost',
        description: '±5% print time prediction, ±3% material usage. Know exactly what each print will cost in time and material.',
      },
      {
        icon: Camera,
        title: 'Real-Time Monitoring',
        description: 'AI watches your print through standard cameras. Detects spaghetti, detachment, and layer shifts within seconds  -  often while still recoverable.',
      },
      {
        icon: Wifi,
        title: 'Universal Connectivity',
        description: 'Connect any printer: OctoPrint, Klipper/Moonraker, Bambu Lab, Prusa Connect, Formlabs, Creality Cloud, Duet, and more.',
      },
      {
        icon: BarChart3,
        title: 'Fleet Management',
        description: 'For print farms: AI orchestrates job routing, predicts queue times, balances loads, and schedules predictive maintenance.',
      },
    ],
  },
  {
    id: 'scan-to-cad',
    icon: ScanLine,
    title: 'Scan to CAD',
    tagline: 'Physical to parametric in minutes',
    description: 'Point your phone at any object. AI reconstructs not just a mesh, but an editable parametric model with proper feature trees. Reverse engineering for everyone.',
    gradient: 'from-success-500 to-warning-500',
    features: [
      {
        icon: Smartphone,
        title: 'Mobile Photogrammetry',
        description: 'Any smartphone becomes a 3D scanner. AI guides you for optimal coverage, handles blur detection, and densifies point clouds.',
      },
      {
        icon: Box,
        title: 'Feature Recognition',
        description: '99%+ accuracy on planar faces, 98%+ on cylinders, 97%+ on holes. AI identifies primitives and converts them to parametric features.',
      },
      {
        icon: Layers,
        title: 'Parametric Rebuild',
        description: 'Unlike competitors generating only mesh, 3DMakerly produces editable models. Modify dimensions, add features, create variants.',
      },
      {
        icon: GitBranch,
        title: 'Constraint Inference',
        description: 'AI determines relationships between features  -  parallel faces, concentric holes, symmetric patterns  -  and maintains them through edits.',
      },
      {
        icon: Wand2,
        title: 'Surface Reconstruction',
        description: 'From noisy scans to clean NURBS surfaces. AI denoises, detects features, fits primitives, and builds proper CAD geometry.',
      },
      {
        icon: CheckCircle,
        title: 'Quality Comparison',
        description: 'Scan your prints and compare to CAD. Get deviation maps, dimensional analysis, and quality scores automatically.',
      },
    ],
  },
  {
    id: 'marketplace',
    icon: Store,
    title: 'MakerHub Marketplace',
    tagline: 'Discover, sell, and fulfill',
    description: 'AI-powered discovery surfaces the perfect model from millions. Creators earn from downloads and print-on-demand. Buyers get models that work.',
    gradient: 'from-warning-500 to-primary-500',
    features: [
      {
        icon: Search,
        title: 'Semantic Search',
        description: '"Organic style lamp for PETG under £5"  -  describe what you need in natural language. AI understands context, style, and constraints.',
      },
      {
        icon: Image,
        title: 'Visual Similarity',
        description: 'Upload an image to find similar models. AI matches style, geometry, and functional characteristics across the entire catalog.',
      },
      {
        icon: ShoppingCart,
        title: 'Instant Checkout',
        description: 'Download immediately or send to print-on-demand. Secure payments, instant delivery, automatic format conversion.',
      },
      {
        icon: Truck,
        title: 'Print-on-Demand Network',
        description: 'Don\'t own a printer? Global network of verified print providers. AI routes to optimal provider for quality, speed, and cost.',
      },
      {
        icon: BarChart3,
        title: 'Creator Analytics',
        description: 'Data-driven insights help creators price optimally, understand demand, and identify trending styles and categories.',
      },
      {
        icon: Sparkles,
        title: 'AI Pricing Guidance',
        description: 'Dynamic pricing suggestions based on demand, complexity, competition, and market trends. Maximise your earnings.',
      },
    ],
  },
]

const technicalSpecs = [
  { label: 'CAD Kernel', value: 'Open CASCADE + Rust' },
  { label: 'File Formats', value: 'STEP, STL, 3MF, OBJ, GLTF' },
  { label: 'AI Models', value: 'MakerLM, Maker3D-Gen' },
  { label: 'Inference', value: 'Cloud + Edge (offline)' },
  { label: 'Collaboration', value: 'Real-time CRDT' },
  { label: 'API', value: 'GraphQL + REST' },
]

export default function FeaturesPage() {
  return (
    <div className="min-h-screen bg-surface-950">
      <MarketingNav />
      
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 gradient-mesh opacity-30" />
        <div className="absolute inset-0 grid-pattern" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 mb-8"
            >
              <Zap className="w-4 h-4 text-primary-400" />
              <span className="text-sm font-medium text-primary-300">Platform Capabilities</span>
              <BetaBadge size="sm" />
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-5xl md:text-6xl font-bold text-white leading-tight mb-6"
            >
              AI isn't a feature.
              <br />
              <span className="text-gradient">It's the foundation.</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-surface-300 max-w-2xl mx-auto"
            >
              Traditional CAD bolts AI onto legacy architecture. 3DMakerly inverts this  -  
              the AI system is the platform, with CAD as one interface to underlying intelligence.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Feature Categories */}
      {featureCategories.map((category, categoryIndex) => (
        <section 
          key={category.id}
          id={category.id}
          className={`py-24 ${categoryIndex % 2 === 1 ? 'bg-surface-900/30' : ''}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Category Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex flex-col md:flex-row md:items-end gap-6 mb-16"
            >
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${category.gradient}`}>
                <category.icon className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-surface-400 mb-2">{category.tagline}</p>
                <h2 className="text-4xl font-bold text-white">{category.title}</h2>
              </div>
              <p className="text-surface-300 max-w-xl md:text-right">{category.description}</p>
            </motion.div>

            {/* Feature Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {category.features.map((feature, featureIndex) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: featureIndex * 0.05 }}
                  className="group p-6 rounded-xl bg-surface-800/50 border border-surface-700/50 hover:border-surface-600 transition-all"
                >
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${category.gradient} opacity-80 group-hover:opacity-100 transition-opacity mb-4`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-surface-400 text-sm leading-relaxed">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Technical Specs */}
      <section className="py-24 border-t border-surface-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Built for professionals</h2>
            <p className="text-surface-400">Enterprise-grade infrastructure with modern architecture</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {technicalSpecs.map((spec) => (
              <div key={spec.label} className="text-center p-6 rounded-xl bg-surface-800/30 border border-surface-700/50">
                <p className="text-surface-400 text-sm mb-2">{spec.label}</p>
                <p className="text-white font-semibold">{spec.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-surface-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Experience the future of making
          </h2>
          <p className="text-xl text-surface-300 mb-10">
            Start with the free tier. Upgrade when you need more power.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/signup">
              <Button variant="ai" size="lg">
                <Sparkles className="w-5 h-5" />
                Start Free
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="secondary" size="lg">
                View Pricing
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
